import {
  SCENARIO_TYPES,
  SCENARIO_TYPE_KEYS,
  resultRouteSegment,
} from "@/lib/role-plays/scenario-types";
import { parseIntegerId } from "@/lib/scenario-tools/scenario-maintenance-config";

/**
 * The seven places a practice result can live, keyed by the slug its result
 * page and APIs use ("plays", "cold-call", "on-the-fly", …).
 *
 * Six are the scenario types and read their columns from
 * `lib/role-plays/scenario-types.js`. On-the-fly is not a scenario type (no
 * scenario table, no assignment), but its results sit in SimulationResult
 * beside cold-call's, pointing at a Play by id, so it is named here beside the
 * table read. The result pages, the report lists, the mobile API, the share
 * registry and the feedback threads each kept their own copy of this map.
 *
 * Client-safe: no Prisma. The reads live in `lib/results/result-access.js`.
 *
 * @typedef {object} ResultKind
 * @property {string|null} type  RolePlayType, null for on-the-fly
 * @property {string} route  the `/dashboard/<route>/results/:id` slug
 * @property {string} model  Prisma delegate holding the result
 * @property {object} where  rows of this kind within `model`
 * @property {string} scenarioIdField  the result's column naming its scenario
 * @property {string|null} scenarioRelation  the result → scenario relation
 * @property {string} scenarioKey  where the scenario sits on a loaded row
 * @property {string} scenarioModel  Prisma delegate holding the scenario
 * @property {string} titleField  the scenario's title column
 * @property {string} personaRelation
 * @property {string} userRelation
 * @property {"int"|"cuid"} idType
 */

const MAX_INT4 = 2_147_483_647;

// Prisma model names are the table names here (the schema maps none of these),
// so the raw-SQL reports can name a table from a delegate.
export function prismaTableName(delegate) {
  return delegate.charAt(0).toUpperCase() + delegate.slice(1);
}

function kindFromScenarioType(type) {
  const row = SCENARIO_TYPES[type];
  return {
    type,
    route: row.route,
    model: row.resultModel,
    where: row.resultWhere,
    scenarioIdField: row.resultScenarioIdField,
    scenarioRelation: row.resultScenarioRelation,
    // A cold-call row has no relation; readers attach its ColdCall under the
    // table name, which is the key every consumer already reads.
    scenarioKey: row.resultScenarioRelation ?? prismaTableName(row.scenarioModel),
    scenarioModel: row.scenarioModel,
    titleField: row.titleField,
    personaRelation: row.resultPersonaRelation,
    userRelation: row.resultUserRelation,
    idType: row.resultIdType,
  };
}

const ON_THE_FLY_KIND = {
  type: null,
  route: resultRouteSegment("ON_THE_FLY"),
  model: SCENARIO_TYPES.COLD_CALL.resultModel,
  where: { simulationType: "ON_THE_FLY" },
  scenarioIdField: SCENARIO_TYPES.COLD_CALL.resultScenarioIdField,
  scenarioRelation: null,
  scenarioKey: prismaTableName(SCENARIO_TYPES.PLAY.scenarioModel),
  scenarioModel: SCENARIO_TYPES.PLAY.scenarioModel,
  titleField: SCENARIO_TYPES.PLAY.titleField,
  personaRelation: SCENARIO_TYPES.COLD_CALL.resultPersonaRelation,
  userRelation: SCENARIO_TYPES.COLD_CALL.resultUserRelation,
  idType: SCENARIO_TYPES.COLD_CALL.resultIdType,
};

/** Route slug → ResultKind. */
export const RESULT_KINDS = Object.freeze(
  Object.fromEntries(
    [...SCENARIO_TYPE_KEYS.map(kindFromScenarioType), ON_THE_FLY_KIND].map(
      (kind) => [kind.route, Object.freeze(kind)],
    ),
  ),
);

export const RESULT_KIND_ROUTES = Object.keys(RESULT_KINDS);

/**
 * The kind behind any spelling a result arrives with: the route slug, the
 * RolePlayType enum, a phone key, or `ON_THE_FLY`. Null when it names none.
 */
export function getResultKind(value) {
  const route = resultRouteSegment(value);
  return route && Object.hasOwn(RESULT_KINDS, route) ? RESULT_KINDS[route] : null;
}

/**
 * A result id read whole, or null: a positive int4 for the integer tables
 * (`parseInt` took "12junk" as 12), a non-blank string for management's cuid.
 */
export function parseResultId(kind, value) {
  if (!kind) return null;
  if (kind.idType === "cuid") {
    const id = typeof value === "string" ? value.trim() : "";
    return id && id.length <= 64 ? id : null;
  }
  const id = parseIntegerId(value);
  return id !== null && id > 0 && id <= MAX_INT4 ? id : null;
}

/**
 * A Prisma `where` for results of `kind` that belong to a studio: the studio
 * its scenario belongs to, which also holds for rows saved before results
 * carried a studio. Cold-call and on-the-fly rows have no scenario relation
 * to filter on, so they are read by their own studio column, which every save
 * path sets. The report lists and the team coaching report read this one rule.
 */
export function resultInStudioWhere(kind, workspaceId) {
  return kind.scenarioRelation
    ? { [kind.scenarioRelation]: { workspaceId } }
    : { workspaceId };
}

/** The scenario's title on a loaded row, or null. */
export function resultScenarioTitle(kind, row) {
  return row?.[kind.scenarioKey]?.[kind.titleField] ?? null;
}
